import {AesGCMSymmetricCoreCrypto} from 'metaserver/static/js/encrypted_folder/crypto/aes_gcm_symmetric_core_crypto';
import type {AsymmetricCoreCrypto} from 'metaserver/static/js/encrypted_folder/crypto/asymmetric_core_crypto';
import {P256AsymmetricCoreCrypto} from 'metaserver/static/js/encrypted_folder/crypto/p256_asymmetric_core_crypto';
import type {SymmetricCoreCrypto} from 'metaserver/static/js/encrypted_folder/crypto/symmetric_core_crypto';
import {
  AlgorithmNotFoundError,
  UnknownError,
} from 'metaserver/static/js/encrypted_folder/crypto/errors';
import type {Algorithm} from 'metaserver/static/js/encrypted_folder/crypto/types';
import {SenderAuthP256AsymmetricCoreCrypto} from 'metaserver/static/js/encrypted_folder/crypto/sender_auth_p256_asymmetric_core_crypto';

export const SHA256_HASH_BYTE_SIZE = 32;
// The current algorithm constant should only be used when creating new keys.
export const CURRENT_ASYM_ALGORITHM: Algorithm =
  'HPKE_DHKEM_P256_HKDF_SHA256_HKDF_SHA256_AES256_GCM';
export const CURRENT_ASYM_ALGORITHM_SENDER_AUTH: Algorithm =
  'SENDER_AUTH_HPKE_DHKEM_P256_HKDF_SHA256_HKDF_SHA256_AES256_GCM';
// We have two use cases for symmetric crypto: Encrypting keys and encrypting content.
// They are not guaranteed to remain the same - one can change with the other staying the same.
// Therefore, we need two constants to identify the current algorithm for both.
export const CURRENT_SYM_ALGORITHM_EKMS_KEYS: Algorithm = 'AES256_GCM_EXTRAS';
export const CURRENT_SYM_ALGORITHM_CONTENT: Algorithm = 'AES256_GCM_EXTRAS';

export function createAsymmetricCoreCrypto(algorithm: Algorithm): AsymmetricCoreCrypto {
  switch (algorithm) {
    case 'HPKE_DHKEM_P256_HKDF_SHA256_HKDF_SHA256_AES256_GCM':
      return new P256AsymmetricCoreCrypto();
    case 'SENDER_AUTH_HPKE_DHKEM_P256_HKDF_SHA256_HKDF_SHA256_AES256_GCM':
      return new SenderAuthP256AsymmetricCoreCrypto();
    default:
      throw new AlgorithmNotFoundError();
  }
}

export function createSymmetricCoreCrypto(algorithm: Algorithm): SymmetricCoreCrypto {
  switch (algorithm) {
    case 'AES256_GCM_EXTRAS':
      return new AesGCMSymmetricCoreCrypto();
    default:
      throw new AlgorithmNotFoundError();
  }
}

export function getAlgorithmFromVersion(version: string): Algorithm {
  switch (version) {
    case 'D1':
      return 'HPKE_DHKEM_P256_HKDF_SHA256_HKDF_SHA256_AES256_GCM';
    default:
      throw new AlgorithmNotFoundError();
  }
}

export const sha256 = async (data: Uint8Array<ArrayBuffer>) => {
  const result = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(result);
};

export const calculateHmacSha256 = async (
  key: Uint8Array<ArrayBuffer>,
  data: Uint8Array<ArrayBuffer>
) => {
  try {
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      key,
      {name: 'HMAC', hash: 'SHA-256'},
      false,
      ['sign']
    );
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, data);
    return new Uint8Array(signature);
  } catch (e) {
    // For an HMAC, there should not be an input dependent error possible.
    throw new UnknownError((e as Error).message ?? 'WebCrypto hmac generation failed');
  }
};

export const verifyHmacSha256 = async (
  key: Uint8Array<ArrayBuffer>,
  data: Uint8Array<ArrayBuffer>,
  signature: Uint8Array<ArrayBuffer>
) => {
  try {
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      key,
      {name: 'HMAC', hash: 'SHA-256'},
      false,
      ['verify']
    );
    return await crypto.subtle.verify('HMAC', cryptoKey, signature, data);
  } catch (e) {
    // For an HMAC, there should not be an input dependent error possible.
    throw new UnknownError((e as Error).message ?? 'WebCrypto hmac verification failed');
  }
};
